import ButtonPrimary from 'components/ButtonPrimary'
import ContentBox from 'components/ContentBox'
import TextBodyMedium from 'components/TextBodyBig'
import TextTitle from 'components/TextTitle'
import { View } from 'components/View'
import getActionStrReduxUtil from 'modules/redux/utils/get-action-str'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import hasSuccessStatusReduxUtil from 'modules/redux/utils/has-success-status'
import { STYLES_PADDING } from 'modules/styles/constants/index.cjs'
import { TRAINING_PLAN_KEY } from 'modules/training/models/plan/constants'
import getNamePlanUtil from 'modules/training/models/plan/utils/get-name'
import loadByRecommendedIdPlanAction from 'modules/plan/redux/actions/load-recommended-by-id'
import trainingPlanMessages from 'modules/plan/messages'
import React from 'react'
import Link from 'components/Link'
import PlanMediaImage from 'containers/Plan/PlanMediaImage'
import { IMAGE_ASPECT_RATIO_ID } from 'components/Image/constants'
import Hoverable from 'components/Hoverable'
import getWidthCardUtil from 'components/Card/utils/get-width'
import { logEventAnalyticsUtil } from 'modules/analytics/utils/log-event/index.js'

import { defineMessages } from 'react-intl'
import TrendingIconOverlay from 'components/TrendingIconOverlay/index.js'
import isTrendingModelUtil from 'models/utils/is-trending/index.js'
import getPathnamePlanUtil from 'modules/plan/redux/utils/get-pathname/index.js'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import useIntl from 'modules/component/utils/hooks/useIntl.js'
import { usePreviousHookUtil } from 'modules/component/utils/hooks/usePrevious.js'
import { getByIdPlanSelector } from '@gymaholic/packages'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import { isMobileScreenViewportSelect } from 'modules/viewport/selectors/index.js'

const ANALYTIC_EVENT = {
  VIEW_PLAN: 'View Plan',
  VIEW_PLAN_IMAGE: 'View Plan Image'
}

const messages = defineMessages({
  viewPlan: {
    id: 'PlanCard.viewPlan',
    defaultMessage: 'View plan'
  }
})

/**
 * @param shouldLoad
 * @param id
 * @param shouldShowViewButton
 * @param isClickable
 * @param parent
 * @param isFixedWidth
 * @param imageWidth
 * @param titleHeight
 * @returns {JSX.Element}
 * @constructor
 */
export function PlanCard ({
  shouldLoad,
  id,
  shouldShowViewButton = true,
  isClickable,
  parent,
  isFixedWidth,
  titleHeight,
  imageWidth
}) {
  const dispatch = useDispatch()
  const intl = useIntl()
  const prevId = usePreviousHookUtil(id)
  const value = useSelector(getByIdPlanSelector(id))
  const isMobileScreen = useSelector(isMobileScreenViewportSelect)

  const handleMount = () => {
    if (!shouldLoad) {
      return
    }

    dispatch(
      loadByRecommendedIdPlanAction({
        id
      })
    )
  }

  React.useEffect(() => {
    handleMount()
  }, [])

  React.useEffect(() => {
    if (prevId !== id && shouldLoad) {
      dispatch(
        loadByRecommendedIdPlanAction({
          id
        })
      )
    }
  }, [
    id,
    prevId,
    shouldLoad
  ])

  const handleClickViewPlan = () => {
    logEventAnalyticsUtil(
      parent
        ? getActionStrReduxUtil(parent, ANALYTIC_EVENT.VIEW_PLAN)
        : ANALYTIC_EVENT.VIEW_PLAN
    )
  }

  const handleClickViewPlanImage = () => {
    logEventAnalyticsUtil(
      parent
        ? getActionStrReduxUtil(parent, ANALYTIC_EVENT.VIEW_PLAN_IMAGE)
        : ANALYTIC_EVENT.VIEW_PLAN_IMAGE
    )
  }

  const renderWorkoutsCount = () => {
    return (
      <View style={{ paddingRight: STYLES_PADDING.MEDIUM }}>
        <TextBodyMedium>
          {
            intl.formatMessage(
              trainingPlanMessages.workout, {
                count: getValueReduxUtil(
                  value,
                  TRAINING_PLAN_KEY.WORKOUTS_COUNT
                )
              }
            )
          }
        </TextBodyMedium>
      </View>
    )
  }

  const renderWeeksCount = () => {
    return (
      <TextBodyMedium>
        {
          intl.formatMessage(
            trainingPlanMessages.week, {
              count: getValueReduxUtil(
                value,
                TRAINING_PLAN_KEY.WEEKS_COUNT
              )
            }
          )
        }
      </TextBodyMedium>
    )
  }

  const renderImage = () => {
    const id = getIdReduxUtil(value)
    const width = imageWidth || getWidthCardUtil({
      isMobileScreen,
      isFixedWidth
    })
    const isTrending = !!isTrendingModelUtil(value)

    const elem = (
      <View
        style={{
          width,
          position: 'relative'
        }}
      >
        {!!isTrending && <TrendingIconOverlay />}

        <PlanMediaImage
          id={id}
          aspectRatio={IMAGE_ASPECT_RATIO_ID.WIDE}
        />
      </View>
    )

    if (!isClickable) {
      return elem
    }

    return (
      <Link
        to={getPathnamePlanUtil(value)}
        onClick={handleClickViewPlanImage}
      >
        {elem}
      </Link>
    )
  }

  const renderViewPlan = () => {
    if (!isClickable) {
      return
    }
    return (
      <View
        style={{
          flexDirection: View.DIRECTION.ROW
        }}
      >
        <ButtonPrimary
          url={getPathnamePlanUtil(value)}
          onClick={handleClickViewPlan}
        >
          {
            intl.formatMessage(
              messages.viewPlan
            )
          }
        </ButtonPrimary>
      </View>
    )
  }

  const renderContent = () => {
    if (!value) {
      return
    }

    const ImageWrapper = isClickable ? Hoverable : React.Fragment
    return (
      <View>
        <View style={{ height: titleHeight, overflow: 'hidden' }}>
          <TextTitle>
            {getNamePlanUtil(value)}
          </TextTitle>
        </View>

        <View style={{ paddingBottom: STYLES_PADDING.TINY }}>
          <View
            style={{
              flexDirection: View.DIRECTION.ROW,
              justifyContent: isClickable
                ? null
                : View.JUSTIFY.CENTER,
              width: '100%'
            }}
          >
            {renderWorkoutsCount()}
            {renderWeeksCount()}
          </View>
        </View>

        <View
          mt
          style={{ paddingBottom: STYLES_PADDING.SMALLEST }}
        >
          <ImageWrapper>
            {renderImage()}
          </ImageWrapper>
        </View>
        {shouldShowViewButton && renderViewPlan()}
      </View>
    )
  }

  if (!hasSuccessStatusReduxUtil(value)) {
    return <View />
  }
  return (
    <ContentBox p={0}>
      {renderContent()}
    </ContentBox>
  )
}
